import React, {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

import {deleteExercise} from '../../redux/slices/builder';
import AreYouSureDialog from './AreYouSureDialog';
import ShareDialog from './ShareDialog';
import {selectUserID} from '../../redux/slices/auth';
import {selectUserGroup} from '../../redux/slices/user';
import {FEATURES} from '../../constants';
import {faPencil, faTrashAlt, faCopy, faLink} from '@fortawesome/pro-light-svg-icons';

const ERROR_CODE_IN_USE = '1';
const DEFAULT_DELETED_MESSAGE = 'Exercise deleted';
const DEFAULT_SNACKBAR_SEVERITY = 'success';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ExerciseMenu({
  anchorEl,
  exercise,
  isEditVisible = true,
  isMenuOpen,
  onDeleteComplete,
  toggleCopyExerciseDialog,
  toggleMenu,
}) {
  const history = useHistory();
  const dispatch = useDispatch();

  const [isAreYouSureDialogOpen, toggleAreYouSureDialog] = useState(false);
  const [isSnackbarOpen, toggleSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState(DEFAULT_DELETED_MESSAGE);
  const [snackbarSeverity, setSnackbarSeverity] = useState(DEFAULT_SNACKBAR_SEVERITY);
  const [isShareDialogOpen, toggleShareDialog] = useState(false);

  const userID = useSelector(selectUserID);
  const userGroup = useSelector(selectUserGroup);

  const {_id: exerciseID, userID: exerciseUserID} = exercise || {};
  const isMyExercise = userID === exerciseUserID;
  const shareURL = `${window.location.origin}/exercise/${exerciseID}`;
  const {
    tier: {features},
  } = userGroup;
  const isContentProducerGroup = features.includes(FEATURES.VISIBILITY_PUBLIC);

  function handleClickEditExercise() {
    toggleMenu(false);
    history.push(`/builder/exercise/${exerciseID}`);
  }

  function handleClickGetShareLink() {
    toggleMenu(false);
    toggleShareDialog(true);
  }

  function handleClickCopyExercise() {
    toggleMenu(false);
    toggleCopyExerciseDialog(true);
  }

  function handleClickDeleteExercise() {
    toggleMenu(false);
    toggleAreYouSureDialog(true);
  }

  async function handleDeleteExercise() {
    toggleAreYouSureDialog(false);
    try {
      await dispatch(deleteExercise(exerciseID));
      setSnackbarText(DEFAULT_DELETED_MESSAGE);
      setSnackbarSeverity(DEFAULT_SNACKBAR_SEVERITY);
      if (onDeleteComplete) onDeleteComplete();
    } catch (error) {
      setSnackbarSeverity('error');
      if (error?.response?.data?.code === ERROR_CODE_IN_USE)
        setSnackbarText('Cannot delete an exercise that is used in a lesson.');
      else setSnackbarText('An error occured trying to delete exercise. Try again.');
    }

    toggleSnackbar(true);
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isSnackbarOpen}
        autoHideDuration={4000}
        onClose={toggleSnackbar}
      >
        <Alert severity={snackbarSeverity}>{snackbarText}</Alert>
      </Snackbar>
      <AreYouSureDialog
        isOpen={isAreYouSureDialogOpen}
        handleClose={() => toggleAreYouSureDialog(false)}
        handleClickActionButton={handleDeleteExercise}
        title="Delete exercise"
        message={`Delete "${exercise?.name}"?`}
        actionButtonText="DELETE"
      />
      <ShareDialog open={isShareDialogOpen} handleClose={() => toggleShareDialog(false)} shareURL={shareURL} />

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={() => toggleMenu(false)}>
        <MenuItem onClick={handleClickGetShareLink}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faLink} />
          </ListItemIcon>
          <ListItemText primary="Get Link" />
        </MenuItem>

        {(isMyExercise || isContentProducerGroup) && isEditVisible && (
          <MenuItem onClick={handleClickEditExercise}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faPencil} />
            </ListItemIcon>
            <ListItemText primary="Edit" />
          </MenuItem>
        )}

        {!isMyExercise && !isContentProducerGroup && !!toggleCopyExerciseDialog && (
          <MenuItem onClick={handleClickCopyExercise}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faCopy} />
            </ListItemIcon>
            <ListItemText primary="Make it mine" />
          </MenuItem>
        )}

        {isMyExercise && (
          <MenuItem onClick={handleClickDeleteExercise}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faTrashAlt} className="clr-danger-400" />
            </ListItemIcon>
            <ListItemText primary="Delete" className="clr-danger-400" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
